import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { ArrowRight, Users, Target, MessageCircle, TrendingUp, Star, Play } from "lucide-react";
import CompanyLayout from "@/components/CompanyLayout";
import { DemoBookingModal } from "@/components/DemoBookingModal";
import { SupademoModal } from "@/components/SupademoModal";
import G2RecognitionSection from "@/components/G2RecognitionSection";
import companyLogo from "@/assets/case-studies/beon-ti-ntg-logo.png";
import happyTeamImage from "@/assets/case-studies/happy-employees-team.jpg";
import nataliePhoto from "@/assets/case-studies/natalie-cook.jpeg";
import mmlcLogo from "@/assets/case-studies/mmlc-logo.webp";
import mmlcHeroImage from "@/assets/case-studies/mmlc-hero.jpg";
import { usePostHog } from "@/hooks/usePostHog";

interface CaseStudy {
  id: string;
  slug: string;
  logo?: string;
  company: string;
  headline: string;
  summary: string;
  metric: string;
  industry: string;
  size: string;
  outcome: string;
  available: boolean;
}

const caseStudies: CaseStudy[] = [
  {
    id: "1",
    slug: "ti-ntg",
    logo: companyLogo,
    company: "TI/NTG",
    headline: "How TI/NTG Built a Culture of Employee Recognition and Connection",
    summary: "Discover how a leading logistics company achieved consistent recognition across all teams and locations.",
    metric: "85% of employees recognized monthly",
    industry: "Logistics",
    size: "Enterprise",
    outcome: "Engagement",
    available: true
  },
  {
    id: "2",
    slug: "mmlc",
    logo: mmlcLogo,
    company: "Minding Miracles Learning Center",
    headline: "How Minding Miracles Learning Center Brought Every Building Together with Assembly",
    summary: "Building bonds beyond classrooms with unified recognition across three preschools and an Adult Center.",
    metric: "Company-wide participation",
    industry: "Early Childhood Education",
    size: "Small Business",
    outcome: "Culture",
    available: true
  }
];

const CaseStudiesHub = () => {
  const [activeFilter, setActiveFilter] = useState("All");
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isSupademoModalOpen, setIsSupademoModalOpen] = useState(false);
  const { posthog } = usePostHog();

  const filters = ["All", "Industry", "Company Size", "Outcomes"];

  const filteredStudies = caseStudies; // For now, show all

  return (
    <CompanyLayout
      title="Customer Case Studies & Success Stories"
      description="Explore how companies across industries use Assembly to celebrate wins, connect teams, and build thriving cultures. Real teams, real results."
    >
      <div className="bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-gray-50 via-white to-blue-50 pt-24 md:pt-32 pb-20 relative overflow-hidden">
          {/* Subtle background elements */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-20 left-10 w-20 h-20 bg-blue-500 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-20 w-32 h-32 bg-purple-500 rounded-full blur-3xl"></div>
          </div>

          <div className="container mx-auto px-4 max-w-5xl text-center relative z-10">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Real Teams. Real Results.
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed">
              Explore how companies across industries use Assembly to celebrate wins, connect teams, and build thriving cultures.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button 
                size="lg" 
                className="group relative bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-4 px-8 rounded-xl shadow-lg shadow-blue-600/25 hover:shadow-xl hover:shadow-blue-600/40 transition-all duration-300 hover:scale-[1.02]" 
                onClick={() => setIsDemoModalOpen(true)}
              >
                <span className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                <span className="relative z-10 flex items-center justify-center gap-2">
                  Book a Demo
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                className="group relative border-2 border-blue-200 bg-white/80 backdrop-blur-sm text-blue-700 hover:text-blue-800 hover:bg-blue-50/80 hover:border-blue-300 font-semibold py-4 px-8 rounded-xl shadow-md shadow-blue-100/50 hover:shadow-lg hover:shadow-blue-200/60 transition-all duration-300 hover:scale-[1.02]" 
                onClick={() => {
                  posthog?.capture('see_it_in_action_clicked', {
                    page: '/resources/case-studies',
                    section: 'hero',
                    button_text: 'See it in Action',
                    url: window.location.href,
                    opens_modal: true
                  });
                  setIsSupademoModalOpen(true);
                }}
              >
                <span className="absolute inset-0 bg-gradient-to-r from-blue-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                <span className="relative z-10 flex items-center justify-center gap-2">
                  See it in Action
                  <Play className="w-4 h-4 transition-transform duration-300 group-hover:scale-110" fill="currentColor" />
                </span>
              </Button>
            </div>
            
            {/* G2 Rating */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 items-center justify-center">
              <div className="flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                <span className="text-sm font-semibold text-gray-700">4.9 / 5 on G2</span>
                <img src="/lovable-uploads/e68d106f-69a9-45e8-8624-7f09b1ae1e6f.png" alt="G2 Logo" className="h-5 w-5" />
              </div>
              <a href="https://www.g2.com/products/assembly-2021-12-09/reviews" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                <span className="text-sm font-semibold text-gray-500">3000+ reviews</span>
              </a>
            </div>
          </div>
        </section>

        {/* Featured Stories Section */}
        <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-purple-50">
          <div className="container mx-auto px-4 max-w-6xl">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
              Featured Stories
            </h2>
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-gray-100">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <img 
                    src={companyLogo} 
                    alt="TI/NTG" 
                    className="h-12 mb-6 object-contain object-left"
                  />
                  <blockquote className="text-2xl text-gray-800 font-medium mb-6 italic">
                    "Recognition became part of our daily rhythm."
                  </blockquote>
                  <div className="flex items-center gap-4 mb-6">
                    <img 
                      src={nataliePhoto} 
                      alt="Natalie Cook" 
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-bold text-gray-900">Natalie Cook</p>
                      <p className="text-gray-600">VP, Employee Experience, TI/NTG</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 mb-8">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-blue-600" />
                      <span className="font-semibold text-gray-900">85% recognized monthly</span>
                    </div>
                  </div>
                  <a href="/resources/case-studies/ti-ntg">
                    <Button className="group">
                      Read Full Story
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </a>
                </div>
                <div className="aspect-[4/3] rounded-2xl shadow-xl overflow-hidden">
                  <img 
                    src={happyTeamImage} 
                    alt="Happy employees collaborating" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* G2 Recognition Section */}
        <G2RecognitionSection />

        {/* Main Grid - Case Studies */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 max-w-7xl">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
              All Case Studies
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredStudies.map((study) => (
                <div key={study.id}>
                  {study.available ? (
                    <a
                      href={`/resources/case-studies/${study.slug}`}
                      className="block group h-full"
                    >
                      <CaseStudyCard study={study} />
                    </a>
                  ) : (
                    <div className="h-full opacity-60 cursor-not-allowed">
                      <CaseStudyCard study={study} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Footer */}
        <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-500 to-purple-600 relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxIDAgNiAyLjY5IDYgNnMtMi42OSA2LTYgNi02LTIuNjktNi02IDIuNjktNiA2LTZ6TTI0IDQyYzMuMzEgMCA2IDIuNjkgNiA2cy0yLjY5IDYtNiA2LTYtMi42OS02LTYgMi42OS02IDYtNnoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjA1Ii8+PC9nPjwvc3ZnPg==')] opacity-30"></div>
          <div className="container mx-auto px-4 max-w-4xl text-center relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Your story could be next.
            </h2>
            <p className="text-xl text-white/90 mb-10">
              Join thousands of teams building better workplace cultures with Assembly.
            </p>
            <div className="flex justify-center">
              <Button 
                size="lg" 
                variant="secondary" 
                className="group"
                onClick={() => setIsDemoModalOpen(true)}
              >
                Book a Demo
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>
          </div>
        </section>
      </div>
      
      {/* Modals */}
      <DemoBookingModal 
        open={isDemoModalOpen} 
        onOpenChange={setIsDemoModalOpen}
      />
      <SupademoModal 
        open={isSupademoModalOpen} 
        onOpenChange={setIsSupademoModalOpen}
      />
    </CompanyLayout>
  );
};

// Case Study Card Component
const CaseStudyCard: React.FC<{ study: CaseStudy }> = ({ study }) => {
  return (
    <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden h-full flex flex-col transition-all duration-300 hover:shadow-xl hover:border-blue-200 hover:-translate-y-1">
      <div className="aspect-[16/9] overflow-hidden">
        <img 
          src={study.slug === 'mmlc' ? mmlcHeroImage : happyTeamImage} 
          alt="Team collaboration" 
          className="w-full h-full object-cover"
        />
      </div>
      
      <div className="p-8 flex flex-col flex-grow">
        <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight">
          {study.headline}
        </h3>
        
        <p className="text-gray-600 mb-6 flex-grow leading-relaxed">
          {study.summary}
        </p>
        
        <div className="mt-auto">
          <div className="text-lg font-bold text-blue-600 mb-4">
            {study.metric}
          </div>
          
          <div className="flex items-center gap-2 text-blue-600 font-medium group-hover:gap-3 transition-all">
            {study.available ? (
              <>
                <span>Read Story</span>
                <ArrowRight className="h-4 w-4" />
              </>
            ) : (
              <span className="text-gray-400">Coming Soon</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseStudiesHub;
